import client from "@/api/openapi-ts";
import type { components } from "@/api/schema.d.ts";
import { titleize } from "@/helpers/filters";
import type { DisabledPlatformFeature } from "@/helpers/platformFeatures";
import { defineStore, acceptHMRUpdate } from "pinia";
import { computed, ref } from "vue";

type PlatformFeature = components["schemas"]["PlatformFeature"];
type PlatformFeatureStatus = components["schemas"]["PlatformFeatureStatus"];
type PlatformFeaturesUpdateRequest =
  components["schemas"]["PlatformFeaturesUpdateRequest"];

export const usePlatformFeatureStore = defineStore("platformFeature", () => {
  const loading = ref(false);
  const saving = ref(false);
  const loaded = ref(false);
  const disabledFeatures = ref<DisabledPlatformFeature[]>([]);

  const disabledKeys = computed(
    () => new Set(disabledFeatures.value.map((feature) => feature.key)),
  );

  const isDisabled = (key: string | null | undefined) =>
    !!key && disabledKeys.value.has(key);

  const nameFor = (key: string | null | undefined) => {
    if (!key) return null;
    return (
      disabledFeatures.value.find((feature) => feature.key === key)?.name ??
      titleize(key)
    );
  };

  const applyDisabledKeys = (keys: DisabledPlatformFeature[]) => {
    disabledFeatures.value = keys;
  };

  let statusRequest: Promise<void> | null = null;

  const loadStatus = async () => {
    if (statusRequest) return statusRequest;
    statusRequest = (async () => {
      try {
        const { data, error } = await client.GET(
          "/api/v1/platform-features/status",
        );
        if (error) {
          throw new Error("Failed to load platform feature status.");
        }
        const status: PlatformFeatureStatus | undefined = data;
        applyDisabledKeys(status?.disabled_features ?? []);
        loaded.value = true;
      } catch (error) {
        console.error(error);
        throw error;
      } finally {
        statusRequest = null;
      }
    })();
    return statusRequest;
  };

  return {
    loading,
    saving,
    loaded,
    disabledFeatures,
    disabledKeys,
    isDisabled,
    nameFor,
    loadStatus,

    index: async () => {
      loading.value = true;
      try {
        await loadStatus();
      } finally {
        loading.value = false;
      }
    },

    updateFeatures: async (
      updates: PlatformFeaturesUpdateRequest["features"],
    ) => {
      if (!updates.length) return;
      saving.value = true;
      try {
        const { data, error } = await client.PATCH(
          "/api/v1/platform-features",
          { body: { features: updates } },
        );
        if (error) {
          throw new Error("Failed to update platform features.");
        }
        const rows: PlatformFeature[] = data ?? [];
        applyDisabledKeys(
          rows.filter((row) => !row.enabled).map((row) => ({ key: row.key })),
        );
        loaded.value = true;
      } finally {
        saving.value = false;
      }
    },
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(usePlatformFeatureStore, import.meta.hot),
  );
}
